import { NEW_BLOCK_ID } from '@unifyapps/defs/constants';
import type { BlockId, BlockType, ComponentsUnionType } from '@unifyapps/defs/types/block';
import type {
  CardComponentType,
  ContainerComponentType,
  StackComponentType,
} from '@unifyapps/defs/types/blocks';
import type { SlotType } from '@unifyapps/defs/types/slot';
import _keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import { DependencyHelper } from '../dependency/helpers/DependencyHelper';
import type { BuildDependencyGraphItem } from '../dependency/utils/buildDependencyGraph';
import { FUNCTION_PREFIX, INTERNALS_PARAMETERS_KEY } from '../no-code/const';
import type { DynamicAndConditionalPathHandler } from '../no-code/context/DependencyGraphProvider/handlers';
import {
  DEPENDENCY_KEYS_TO_OMIT_DURING_SERIALIZATION,
  NoCodeDependencyKeys,
} from '../no-code/dependency/const';
import { ComputeContextKeys } from '../no-code/hooks/computeContext/const';
import { BlockDataAttributes } from '../no-code/hooks/useBlockDataAttributes';
import { isEmptyBlock } from '../no-code/utils/block';
import getBlockPipeline from '../no-code/utils/blockPipeline';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class -- false positive
export class BlockHelper {
  static getComponentBlockIds(block: BlockType) {
    if ('blockId' in block.component.content) {
      return [(block.component as { content: { blockId: BlockId } }).content.blockId];
    }

    // @ts-expect-error -- blockIds and config.blockIds are present
    return (block.component.blockIds ??
      (block.component.content as { blockIds: string[] } | undefined)?.blockIds) as
      | BlockId[]
      | undefined;
  }

  static getBlockSlots(block: BlockType) {
    return ('slots' in block.component ? block.component.slots : {}) as
      | Record<string, SlotType | undefined>
      | undefined;
  }

  static getBlockItems(block: BlockType) {
    return 'content' in block.component && 'items' in block.component.content
      ? block.component.content.items
      : [];
  }

  static shouldAddEmptyComponent(blockId?: BlockId) {
    // NOTE: if blockId is null, it means it's an empty block, and we should add an add component button
    return isEmptyBlock(blockId);
  }

  static getBlocksMapById(blocks: BlockType[]) {
    return _keyBy(blocks, 'id');
  }

  static getBlocksByComponentTypes(
    blocks: BlockType[],
    componentType: ComponentsUnionType['componentType'] | ComponentsUnionType['componentType'][],
  ) {
    const componentTypes = Array.isArray(componentType) ? componentType : [componentType];

    return blocks.filter((block) => componentTypes.includes(block.component.componentType));
  }

  static getDynamicPaths(block?: BlockType) {
    return block?.[NoCodeDependencyKeys.DynamicPaths] as BlockType['dP'] | undefined;
  }

  static getConditionalPaths(block?: BlockType) {
    return block?.[NoCodeDependencyKeys.ConditionalPaths] as BlockType['cP'] | undefined;
  }

  static getDependsOn(block?: BlockType) {
    return block?.[NoCodeDependencyKeys.DependsOn] as BlockType['dpOn'] | undefined;
  }

  /**
   * Checks if a block has any dynamic paths.
   * This is useful to determine if a block needs dynamic context to be rendered.
   *
   * @param block - The block to check
   * @returns true if the block has dynamic paths
   */
  static hasDynamicPaths(block?: BlockType): boolean {
    const dynamicPaths = BlockHelper.getDynamicPaths(block);
    return Boolean(dynamicPaths?.length);
  }

  /**
   * Checks if a block has any conditional paths.
   *
   * @param block - The block to check
   * @returns true if the block has conditional paths
   */
  static hasConditionalPaths(block?: BlockType): boolean {
    const conditionalPaths = BlockHelper.getConditionalPaths(block);
    return Boolean(conditionalPaths?.length);
  }

  static isBlockStackOrContainer(
    block: BlockType,
  ): block is BlockType<StackComponentType | ContainerComponentType> {
    return (
      block.component.componentType === 'Stack' || block.component.componentType === 'Container'
    );
  }

  static extractBlockDependencyKeys(
    dep?: BuildDependencyGraphItem<DynamicAndConditionalPathHandler>,
  ): Pick<BlockType, 'cP' | 'dP' | 'dpOn'> {
    return {
      [NoCodeDependencyKeys.DependsOn]: DependencyHelper.getDependsOn(dep),
      [NoCodeDependencyKeys.ConditionalPaths]: DependencyHelper.getConditionalPaths(dep),
      [NoCodeDependencyKeys.DynamicPaths]: DependencyHelper.getDynamicPaths(dep),
    };
  }

  static getDataSourceIds(block?: BlockType) {
    if (!block) {
      return [];
    }

    if (
      block.component.componentType === 'Filters' ||
      block.component.componentType === 'CategoryFilter'
    ) {
      // we are also spreading block.dataSourceIds
      // because some of the other data source can be referenced in the block via pill
      return Array.from(
        new Set([...(block.component.content.dataSourceIds ?? []), ...(block.dataSourceIds ?? [])]),
      );
    }

    return Array.from(new Set(block.dataSourceIds ?? []));
  }

  static getPageFunctionIds(block?: BlockType) {
    const dependsOn = BlockHelper.getDependsOn(block);
    return (
      dependsOn?.filter((dep) => dep.id.startsWith(FUNCTION_PREFIX)).map((dep) => dep.id) || []
    );
  }

  /**
   * Returns a copy of the block with events and dependency-related keys removed.
   * This is used when we need to reference data source IDs without including
   * computed dependency information or event handlers because events are fired on demand
   * and deps keys are not the actual source code.
   *
   * @param block - The block to process
   * @returns A filtered copy of the block without events and dependency keys
   */
  static getBlockForReferencingDataSourceIds(block: BlockType) {
    const { events: _, ...rest } = block;
    return omit(rest, DEPENDENCY_KEYS_TO_OMIT_DURING_SERIALIZATION);
  }

  static getAdaptedBlockForBuilderDependencyGraph(block: BlockType) {
    // remove the dependency keys
    const cleanBlock = omit(block, DEPENDENCY_KEYS_TO_OMIT_DURING_SERIALIZATION);
    let blockCopy = { ...cleanBlock };
    const { blocksPipeline } = getBlockPipeline();

    blocksPipeline.execute({
      block: blockCopy as BlockType,
      handleSlotsBlock: (slotBlock) => {
        // Handle slots (Card-like components)
        if (blockCopy.component) {
          blockCopy = {
            ...blockCopy,
            component: {
              ...blockCopy.component,
              slots: Object.fromEntries(
                Object.entries(slotBlock.component.slots || {}).map(([key, slot]) => [
                  key,
                  omit(slot, ['blockId', 'blockIds']),
                ]),
              ),
            } as CardComponentType,
          };
        }
      },
      handleContentBlock: (tabBlock) => {
        // Handle content.items (Tabs-like components)
        if (
          blockCopy.component &&
          'content' in blockCopy.component &&
          'items' in tabBlock.component.content
        ) {
          blockCopy = {
            ...blockCopy,
            component: {
              ...blockCopy.component,
              content: {
                ...blockCopy.component.content,
                items: tabBlock.component.content.items?.map((item) =>
                  omit(item, ['blockId', 'blockIds']),
                ),
              },
            } as typeof blockCopy.component,
          };
        }
      },
      handleBlockIdsBlock: () => {
        // Handle content.blockIds (Stack-like components)
        if (blockCopy.component && 'content' in blockCopy.component) {
          blockCopy = {
            ...blockCopy,
            component: {
              ...blockCopy.component,
              content: omit(blockCopy.component.content, ['blockIds']),
            } as typeof blockCopy.component,
          };
        }
      },
      handleBlockIdBlock: () => {
        // Handle content.blockId (Repeatable-like components)
        if (blockCopy.component && 'content' in blockCopy.component) {
          blockCopy = {
            ...blockCopy,
            component: {
              ...blockCopy.component,
              content: omit(blockCopy.component.content, ['blockId']),
            } as typeof blockCopy.component,
          };
        }
      },
    });

    // Remove other hierarchy-related fields
    return omit(blockCopy, ['parentId', 'blockId', 'parentBlockId']);
  }

  static isLayoutBlock(block: BlockType) {
    return (
      block.component.componentType === 'Stack' || block.component.componentType === 'Container'
    );
  }

  static isLayoutWithAddComponentBlock(block: BlockType) {
    return Boolean(
      BlockHelper.isLayoutBlock(block) &&
      BlockHelper.getComponentBlockIds(block)?.includes(NEW_BLOCK_ID),
    );
  }

  static getContainerEl({
    containerId,
    pageEl,
    pageScopeKey,
  }: {
    containerId?: string;
    pageEl?: HTMLDivElement | null;
    pageScopeKey?: string;
  }) {
    if (!containerId) return null;

    const blockSelector = `[${BlockDataAttributes.BlockId}="${containerId}"]`;

    // first, search within the provided page element, narrowing to the page scope when present
    if (pageEl) {
      const scopeEl = pageScopeKey
        ? (pageEl.querySelector(`[${BlockDataAttributes.PageScopeKey}="${pageScopeKey}"]`) ??
          pageEl)
        : pageEl;

      const containerEl = scopeEl.querySelector(blockSelector);
      if (containerEl) return containerEl as HTMLDivElement;
    }

    // detached blocks render outside pageEl, so fall back to scanning every
    // page-scope-key instance in the document — the descendant combinator returns the first
    // container nested under any matching scope, so it works across duplicate scope instances
    if (pageScopeKey) {
      const detachedEl = document.querySelector(
        `[${BlockDataAttributes.PageScopeKey}="${pageScopeKey}"] ${blockSelector}`,
      );
      if (detachedEl) return detachedEl as HTMLDivElement;
    }

    return null;
  }

  static getBlockSessionStoragePath({
    interfacePageId,
    blockId,
  }: {
    interfacePageId: string;
    blockId: string;
  }) {
    return `${ComputeContextKeys.SESSION_STORAGE}[${INTERNALS_PARAMETERS_KEY}][${interfacePageId}][${blockId}]`;
  }

  static getFirstBlockFromStack(block?: BlockType) {
    if (block && block.component.componentType === 'Stack') {
      return block.component.content.blockIds?.[0] ?? '';
    }
    return '';
  }
}
